'use client'

import { useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { sendRequest, uploadFiles } from '@/utils/request'
import { downscaleImage } from '@/utils/image'
import { toast } from '@/lib/toast'

export function useCustomerSettings(customer: any) {
  const router = useRouter()
  const { data: session, update: updateSession } = useSession()

  const [firstName, setFirstName] = useState(customer?.firstName)
  const [lastName, setLastName] = useState(customer?.lastName)
  const [email, setEmail] = useState(customer?.email)
  const [phone, setPhone] = useState(customer?.phone)
  const [avatar, setAvatar] = useState(customer?.avatar)
  const [preview, setPreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isImageUploaded, setIsImageUploaded] = useState(false)
  const inputFile = useRef<HTMLInputElement>(null)

  const handleFileChange = async (e: any) => {
    const original = e.target.files[0]
    if (!original) return

    if (!original.type.startsWith('image/')) return toast.error('Зөвхөн зураг оруулна уу!')

    setIsImageUploaded(true)
    try {
      // Shrink first, so a 12MP phone photo is not a multi-megabyte upload for
      // an avatar rendered at 64px. Returns the original when it cannot help.
      const file = await downscaleImage(original)

      // The API caps uploads at 10MB. Whatever is still over that after the
      // downscale — an animated GIF, or HEIC that could not be decoded here —
      // is stopped now rather than dying as a proxy 413 further down.
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Зурагны хэмжээ 10MB-с их байна!')
        e.target.value = ''
        return
      }

      const reader = new FileReader()
      reader.onload = (ev) => {
        setPreview(ev.target?.result as string)
      }
      reader.readAsDataURL(file)

      const formData = new FormData()
      formData.append('image', file)

      const resUpload = await uploadFiles({ formData })

      if (!resUpload) {
        toast.error('Зураг хуулахад алдаа гарлаа')
        setPreview(null)
        e.target.value = ''
        return
      }
      if (resUpload.error) {
        toast.error(resUpload.error)
        setPreview(null)
        e.target.value = ''
        return
      }
      const uploadedImage = resUpload.data[0]

      // ponytail: persist the avatar right away instead of waiting for Хадгалах.
      // PUT /customer patches field-by-field (`if(avatar)` in customer.service.js),
      // so sending avatar alone leaves unsaved name/phone edits in the form untouched.
      const res = await sendRequest({ url: `/customer`, method: 'PUT', body: { avatar: uploadedImage } })
      if (!res || res.error) {
        toast.error(res?.error || 'Зураг хадгалахад алдаа гарлаа')
        setPreview(null)
        e.target.value = ''
        return
      }

      setAvatar(uploadedImage)
      if (session) {
        await updateSession({ ...session, user: { ...session.user, avatar: uploadedImage } })
      }
      toast.success('Зураг амжилттай хадгалагдлаа')
      e.target.value = ''
      router.refresh()
    } catch (error) {
      toast.error('Зураг хуулахад алдаа гарлаа')
      setPreview(null)
      e.target.value = ''
    } finally {
      setIsImageUploaded(false)
    }
  }

  const selectFile = () => {
    inputFile?.current?.click()
  }

  const handleSave = async () => {
    if (!firstName) return toast.error('Өөрийн нэрийг оруулна уу')
    if (!lastName) return toast.error('Өөрийн овгийг оруулна уу')
    if (!email) return toast.error('И-мэйл хаягаа оруулна уу')
    if (!phone) return toast.error('Утасны дугаараа оруулна уу')
    setLoading(true)
    const res = await sendRequest({ url: `/customer`, method: 'PUT', body: { firstName, lastName, phone, avatar } })
    setLoading(false)
    if (!res) return toast.error('Мэдээллийг хадгалахад алдаа гарлаа')
    if (res.error) return toast.error(res.error)
    if (session) {
      await updateSession({ ...session, user: { ...session.user, firstName, lastName, phone, avatar } })
    }
    toast.success('Мэдээллийг амжилттай хадгаллаа')
    router.refresh()
  }

  return {
    firstName, setFirstName,
    lastName, setLastName,
    email, setEmail,
    phone, setPhone,
    avatar, preview,
    loading, isImageUploaded,
    inputFile,
    handleFileChange, selectFile, handleSave,
  }
}
